// frontend/src/components/team/WorkflowsTab.tsx
import AddIcon from '@mui/icons-material/AddOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import type { Workflow, WorkflowCreate } from '../../services/api';
import { workflowsApi } from '../../services/api';
import WorkflowCard from './WorkflowCard';
import WorkflowDialog from './WorkflowDialog';
import WorkflowRunHistoryDrawer from './WorkflowRunHistoryDrawer';

interface Props {
  teamId: string;
}

const WorkflowsTab: React.FC<Props> = ({ teamId }) => {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Workflow | null>(null);
  const [historyWorkflow, setHistoryWorkflow] = useState<Workflow | null>(null);
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());

  const { data: workflows = [], isLoading } = useQuery({
    queryKey: ['workflows', teamId],
    queryFn: () => workflowsApi.list(teamId).then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data: WorkflowCreate) => workflowsApi.create(teamId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', teamId] });
      setDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: WorkflowCreate }) =>
      workflowsApi.update(teamId, id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', teamId] });
      setDialogOpen(false);
      setEditTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => workflowsApi.delete(teamId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workflows', teamId] }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => workflowsApi.archive(teamId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workflows', teamId] }),
  });

  const handleToggleActive = async (id: string, active: boolean) => {
    await workflowsApi.update(teamId, id, { is_active: active });
    qc.invalidateQueries({ queryKey: ['workflows', teamId] });
  };

  const handleRunNow = async (id: string) => {
    setRunningIds((s) => new Set(s).add(id));
    try {
      await workflowsApi.triggerRun(teamId, id);
      qc.invalidateQueries({ queryKey: ['workflows', teamId] });
    } finally {
      setRunningIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  const handleSave = (data: WorkflowCreate) => {
    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  if (isLoading) {
    return <Typography color="text.secondary">Loading workflows...</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Workflows</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={() => {
            setEditTarget(null);
            setDialogOpen(true);
          }}
        >
          New Workflow
        </Button>
      </Box>

      {workflows.length === 0 && (
        <Typography color="text.secondary">
          No workflows yet. Create one to get started.
        </Typography>
      )}

      {workflows.map((wf: Workflow) => (
        <WorkflowCard
          key={wf.id}
          workflow={wf}
          onToggleActive={handleToggleActive}
          onRunNow={handleRunNow}
          onEdit={(w) => {
            setEditTarget(w);
            setDialogOpen(true);
          }}
          onHistory={(w) => setHistoryWorkflow(w)}
          onDelete={(id) => deleteMutation.mutate(id)}
          onArchive={(id) => archiveMutation.mutate(id)}
          isRunning={runningIds.has(wf.id)}
        />
      ))}

      <WorkflowDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditTarget(null);
        }}
        onSave={handleSave}
        initial={editTarget}
        isSaving={createMutation.isPending || updateMutation.isPending}
        teamId={teamId}
      />

      <WorkflowRunHistoryDrawer
        open={historyWorkflow !== null}
        onClose={() => setHistoryWorkflow(null)}
        workflow={historyWorkflow}
        teamId={teamId}
      />
    </Box>
  );
};

export default WorkflowsTab;
